// Catalogue des permissions du backoffice, organisé par section pour que l'UI puisse toujours
// afficher une permission avec son contexte (impossible de confondre deux droits qui portent
// un nom proche s'ils sont affichés sous des en-têtes de section différentes).
//
// Chaque entrée de PERMISSION_SECTIONS a :
//  - pageKey : la clé qui gate l'accès à la page entière (null si la section n'a pas de page
//    dédiée, ex. "Gestion des groupes" qui n'existe qu'au travers de sa permission fine) —
//    c'est la même clé que celle vérifiée par PermissionGate/SidebarContent.
//  - actions : les droits plus fins à l'intérieur de cette page (vérifiés directement au niveau
//    du bouton/composant concerné, ex. isSuperAdmin || userPermissions.includes('clients.delete')).
//
// Un super-admin (is_super_admin === true) a toujours accès à tout, quelles que soient ces
// listes — voir PermissionGate.js. Les clés "app:{client_id}" (accès à une application externe,
// ex. le futur programme de gestion FAQ) sont dynamiques, chargées depuis GET /api/applications,
// et ne figurent pas ici.
export const PERMISSION_SECTIONS = [
    {
        section: 'applications',
        label: 'Applications',
        pageKey: 'applications',
        actions: [
            { key: 'applications.manage', label: 'Éditer une application' },
            { key: 'applications.delete', label: 'Supprimer / activer-désactiver une application' },
            { key: 'applications.technical', label: 'Onglet Information technique (infra, serveurs)' },
            { key: 'applications.pricing.view', label: 'Voir la grille tarifaire' },
            { key: 'applications.pricing.manage', label: 'Éditer la grille tarifaire' },
            { key: 'applications.pricing.discount', label: 'Export PDF avec remise ad-hoc (grille Applications)' },
        ],
    },
    { section: 'prospects', label: 'Prospects', pageKey: 'prospects', actions: [] },
    {
        section: 'clients',
        label: 'Clients',
        pageKey: 'clients',
        actions: [
            { key: 'clients.connector-access', label: "Accès Connector (se connecter dans le logiciel du client)" },
            { key: 'clients.users.manage', label: "Gérer les utilisateurs d'un client (dont pose manuelle de mot de passe)" },
            { key: 'clients.licenses.manage', label: 'Gérer les abonnements/licences (montants négociés)' },
            { key: 'clients.delete', label: 'Supprimer un client' },
            { key: 'support.start_session', label: 'Activer une session de support live (observation + prise de main)' },
        ],
    },
    {
        section: 'support',
        label: 'Support live',
        pageKey: null,
        actions: [
            { key: 'support.view_history', label: "Consulter l'historique des sessions de support (preuve d'accès)" },
        ],
    },
    { section: 'orders', label: 'Commandes', pageKey: 'orders', actions: [] },
    {
        section: 'quotes',
        label: 'Devis',
        pageKey: 'quotes',
        actions: [
            { key: 'quotes.manage', label: 'Créer / révoquer / régénérer un lien de paiement' },
        ],
    },
    {
        section: 'employes',
        label: 'Employés',
        pageKey: 'employes',
        actions: [
            { key: 'employes.manage', label: "Créer/supprimer des comptes employés (jamais un compte super-admin)" },
        ],
    },
    {
        section: 'tickets',
        label: 'Support',
        pageKey: 'tickets',
        actions: [
            { key: 'tickets.support', label: 'Tickets support technique' },
            { key: 'tickets.commercial', label: 'Tickets prise de contact commerciale' },
            { key: 'tickets.manage', label: 'Changer le statut des tickets' },
        ],
    },
    {
        section: 'categories',
        label: 'Catégories',
        pageKey: 'categories',
        actions: [
            { key: 'categories.manage', label: 'Gérer les catégories' },
        ],
    },
    {
        section: 'client-groups',
        label: 'Groupes de clients',
        pageKey: 'client-groups',
        actions: [
            { key: 'client-groups.manage', label: 'Gérer les groupes de clients' },
        ],
    },
    {
        section: 'pricing-plans',
        label: 'Grilles tarifaires',
        pageKey: 'pricing-plans',
        actions: [
            { key: 'pricing-plans.promo-codes.manage', label: 'Gérer les codes promo globaux' },
        ],
    },
    {
        section: 'service-packages',
        label: 'Prestations (sites & SEO)',
        pageKey: 'service-packages',
        actions: [
            { key: 'service-packages.manage', label: 'Gérer les prestations' },
            { key: 'service-packages.pricing.discount', label: 'Export PDF avec remise ad-hoc (grille Prestations)' },
        ],
    },
    {
        section: 'demo-sites',
        label: 'Sites de démo',
        pageKey: 'demo-sites',
        actions: [
            { key: 'demo-sites.credentials', label: 'Révéler/copier les identifiants stockés' },
            { key: 'demo-sites.manage', label: 'Gérer les sites de démo' },
        ],
    },
    {
        section: 'settings',
        label: 'Réglages',
        pageKey: 'settings',
        actions: [
            { key: 'settings.legal-content', label: 'Éditer CGU / CGV / mentions légales' },
        ],
    },
    {
        section: 'groupes',
        label: 'Gestion des groupes',
        pageKey: null,
        actions: [
            { key: 'manage-own-group-members', label: 'Ajouter/retirer des membres, uniquement dans ses propres groupes' },
        ],
    },
];

// Sections (accès à une page entière) — dérivé de PERMISSION_SECTIONS pour les usages existants
// qui n'ont besoin que de la liste plate {key, label}.
export const PERMISSION_KEYS = PERMISSION_SECTIONS
    .filter(s => s.pageKey)
    .map(s => ({ key: s.pageKey, label: s.label }));

// Toutes les permissions fines, à plat — pratique pour les checks isSuperAdmin || X.includes(key).
export const FINE_PERMISSION_KEYS = PERMISSION_SECTIONS.flatMap(s => s.actions);
